import { z } from 'zod';
import { api } from '../../../lib/api/client';

// S18 · Activity › Jobs (07 §5): the jobs table, the queue tiles and the
// carriers' limits, the extension's current job. Mirrored from
// apps/api/src/modules/jobs/jobs-console.dto.ts and extension/dto.ts; unknown
// keys pass through, enums the console only displays stay strings. Relative
// imports: the API's integration spec loads this file at run time.

export const JOB_STATES = ['queued', 'running', 'done', 'failed', 'cancelled'] as const;
export const QUEUE_NAMES = [
  'carrier-reads',
  'vessel-positions',
  'ais-ingest',
  'recompute',
  'outbound',
  'notifications',
  'maintenance',
] as const;
export type QueueName = (typeof QUEUE_NAMES)[number];

export const jobViewSchema = z.looseObject({
  id: z.string(),
  queue: z.string(),
  name: z.string(),
  kind: z.string(),
  state: z.enum(JOB_STATES),
  attempts: z.number(),
  max_attempts: z.number(),
  shipment_id: z.string().nullable(),
  carrier_id: z.string().nullable(),
  conveyance_id: z.string().nullable(),
  run_after: z.string().nullable(),
  started_at: z.string().nullable(),
  finished_at: z.string().nullable(),
  duration_ms: z.number().nullable(),
  error: z.string().nullable(),
  created_by: z.string(),
  created_at: z.string(),
  // What the job is about (added with S18; optional so an older API still parses).
  shipment_reference: z.string().nullable().optional(),
  carrier_code: z.string().nullable().optional(),
  carrier_name: z.string().nullable().optional(),
  conveyance_name: z.string().nullable().optional(),
  conveyance_imo: z.string().nullable().optional(),
});
export type JobView = z.infer<typeof jobViewSchema>;

export const jobListSchema = z.looseObject({
  items: z.array(jobViewSchema),
  next_cursor: z.string().nullable(),
});
export type JobList = z.infer<typeof jobListSchema>;

export const jobActionResultSchema = z.looseObject({ job: jobViewSchema, result: z.string() });

export const queueViewSchema = z.looseObject({
  name: z.string(),
  paused: z.boolean(),
  waiting: z.number().nullable(),
  active: z.number().nullable(),
  delayed: z.number().nullable(),
  failed: z.number().nullable(),
  completed_last_hour: z.number(),
  failed_last_hour: z.number(),
  paused_at: z.string().nullable().optional(),
  paused_by: z.string().nullable().optional(),
  pause_reason: z.string().nullable().optional(),
});
export type QueueView = z.infer<typeof queueViewSchema>;

export const carrierLimitSchema = z.looseObject({
  code: z.string(),
  name: z.string(),
  state: z.string(),
  cooldown_until: z.string().nullable(),
  last_dispatch_at: z.string().nullable(),
  next_read_at: z.string().nullable(),
  reason: z.string(),
});
export type CarrierLimit = z.infer<typeof carrierLimitSchema>;

export const queuesSchema = z.looseObject({
  items: z.array(queueViewSchema),
  carrier_cooldowns: z.array(
    z.looseObject({ code: z.string(), state: z.string(), cooldown_until: z.string().nullable() }),
  ),
  min_hours_between_carrier_reads: z.number().optional(),
  carrier_limits: z.array(carrierLimitSchema).optional(),
});
export type Queues = z.infer<typeof queuesSchema>;

export const extensionClientSchema = z.looseObject({
  client_name: z.string(),
  version: z.string().nullable(),
  last_seen_at: z.string().nullable(),
  state: z.string(),
  current_job_id: z.string().nullable(),
  paused: z.boolean(),
  outdated: z.boolean(),
});
export type ExtensionClient = z.infer<typeof extensionClientSchema>;
export const extensionClientsSchema = z.looseObject({ items: z.array(extensionClientSchema) });

export interface JobFilters {
  queue?: QueueName;
  state?: (typeof JOB_STATES)[number];
}

const enc = encodeURIComponent;

export const jobsApi = {
  list: (filters: JobFilters, limit = 100) => {
    const q = new URLSearchParams();
    if (filters.queue) q.set('queue', filters.queue);
    if (filters.state) q.set('state', filters.state);
    q.set('limit', String(limit));
    return api(`/internal/jobs?${q}`, { schema: jobListSchema });
  },
  get: (id: string) => api(`/internal/jobs/${enc(id)}`, { schema: jobViewSchema }),
  action: (id: string, action: 'retry' | 'cancel' | 'promote') =>
    api(`/internal/jobs/${enc(id)}/${action}`, { method: 'POST', schema: jobActionResultSchema }),
  queues: () => api('/internal/queues', { schema: queuesSchema }),
  pause: (name: string, reason?: string) =>
    api(`/internal/queues/${enc(name)}/pause`, {
      method: 'POST',
      body: reason ? { reason } : {},
      schema: queueViewSchema,
    }),
  resume: (name: string) =>
    api(`/internal/queues/${enc(name)}/resume`, { method: 'POST', schema: queueViewSchema }),
  clients: () => api('/internal/extension/clients', { schema: extensionClientsSchema }),
};

export const jobsKeys = {
  all: ['activity', 'jobs'] as const,
  list: (filters: JobFilters) => ['activity', 'jobs', 'list', filters] as const,
  job: (id: string) => ['activity', 'jobs', 'job', id] as const,
  queues: ['activity', 'jobs', 'queues'] as const,
  clients: ['activity', 'jobs', 'clients'] as const,
};
